import { STALE_CONNECTED_TIMEOUT_MS } from "./constants";

export interface PresenceRecord {
  connected?: boolean;
  lastSeen?: number | null;
}

/**
 * Milliseconds since last presence update.
 * Returns Infinity when lastSeen is missing/invalid.
 */
export function disconnectedDurationMs(
  record: PresenceRecord | null | undefined,
  nowMs: number
): number {
  if (!record || typeof record.lastSeen !== "number") return Infinity;
  return Math.max(0, nowMs - record.lastSeen);
}

/**
 * True when a player should be treated as connected for gameplay/lifecycle logic.
 * A player is effectively connected only if:
 * - connected is not explicitly false, and
 * - lastSeen exists and is within stale timeout.
 */
export function isEffectivelyConnected(
  record: PresenceRecord | null | undefined,
  nowMs: number,
  staleTimeoutMs: number = STALE_CONNECTED_TIMEOUT_MS
): boolean {
  if (!record) return false;
  if (record.connected === false) return false;
  return disconnectedDurationMs(record, nowMs) < staleTimeoutMs;
}

export function countEffectivelyConnected<T extends PresenceRecord>(
  players: Record<string, T> | null | undefined,
  nowMs: number,
  staleTimeoutMs: number = STALE_CONNECTED_TIMEOUT_MS
): number {
  if (!players) return 0;
  return Object.values(players).filter((p) =>
    isEffectivelyConnected(p, nowMs, staleTimeoutMs)
  ).length;
}
